/**
 * The pose gesture — ADR-007 §2.4 made touchable: pick a bone, drag its
 * rotation, and ONE `pose.set` commits on release. Mid-drag the Document is
 * untouched — the gizmo previews on the VIEW only, so Escape-to-cancel is free
 * and always correct (§2.4: nothing outside the Document survives the
 * gesture's end, whichever way it ends). The tool is OFFERED only in pose mode
 * (ADR-006 §13.2: mode scopes tools; §13.0.2: don't show what can't honestly
 * run) — in playback the gesture is unrepresentable, not guarded against.
 *
 * The pick surface IS the skeleton: the octahedral bone visual (seam #3 —
 * bone-visual.ts, the carried v2 beauty asset under the §4.1 material law).
 * Segments and joint beads carry userData.bone; picking a segment selects the
 * bone it articulates. The scaffolding dots of seam #2 are gone.
 * Depends on: three (raycast), three/addons (TransformControls), bone-visual.
 * View-layer only — commits leave through the injected onCommit, in Document
 * vocabulary. Depended on by: viewport (offers/withdraws it on mode).
 */
import { Raycaster, Vector2, Vector3, type Camera, type Object3D, type Scene } from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { createBoneVisual, type BoneDrawState, type BoneGeometrySource } from './bone-visual.js';

export interface PoseToolConfig {
  readonly readOnly?: boolean;
  /** Bones the tool may select/pose — Document vocabulary (rig bone names). */
  readonly boneNames: readonly string[];
  /**
   * The rig's mapped-bone set. The DRAWN vocabulary is a subset of
   * `boneNames`, and which twists survive that cut is the mapper's verdict,
   * never the name's (#70) — bone-vocabulary owns the rule.
   */
  readonly mapped: ReadonlySet<string>;
  /** The authored octahedron/bead geometry, extracted fail-loud at boot. */
  readonly geometry: BoneGeometrySource;
  onSelect(bone: string): void;
  /** ONE commit per gesture, on release — a unit quaternion in bone-local space. */
  onCommit(bone: string, rotation: { x: number; y: number; z: number; w: number }): void;
}

export interface PoseToolDeps {
  readonly scene: Scene;
  readonly camera: Camera;
  readonly domElement: HTMLElement;
  readonly subject: Object3D;
  /** Disable/enable the orbit while the gizmo drags (the two must not fight). */
  setOrbitEnabled(enabled: boolean): void;
}

export interface PoseTool {
  /** Skeleton-visual counts + selection — the smoke's evidence surface. */
  readonly probe: { handleCount: number; segmentCount: number; selected: string | null; dragging: boolean };
  /** Screen-space position of a bone's joint — lets the e2e aim a REAL click. */
  handleScreenXY(bone: string): { x: number; y: number } | null;
  /** Re-seat the skeleton visual on current bone world transforms — per frame. */
  update(): void;
  /** Every drawn skeleton mesh's queue/depth/order — grafted bones must match the body's. */
  drawStates(): Record<string, BoneDrawState>;
  select(bone: string | null): void;
  dispose(): void;
}

export function createPoseTool(deps: PoseToolDeps, config: PoseToolConfig): PoseTool {
  const boneNodes = new Map<string, Object3D>();
  for (const name of config.boneNames) {
    const node = deps.subject.getObjectByName(name);
    if (!node) {
      throw new Error(
        `pose tool: bone "${name}" has no scene node — the rig and the scene disagree ` +
          `(§8: fails loudly at offer time, naming it; materialization should have grown this node).`,
      );
    }
    boneNodes.set(name, node);
  }

  // The skeleton IS the pick surface (seam #3): octahedral segments + joint
  // beads, each stamped with the bone it articulates.
  const visual = createBoneVisual(deps.scene, deps.subject, config.boneNames, config.geometry, config.mapped);

  const gizmo = new TransformControls(deps.camera, deps.domElement);
  gizmo.enabled = !config.readOnly;
  gizmo.setMode('rotate');
  gizmo.setSpace('local');
  gizmo.setSize(0.6);
  deps.scene.add(gizmo.getHelper());

  const probe = {
    handleCount: visual.probe.beadCount,
    segmentCount: visual.probe.segmentCount,
    selected: null as string | null,
    dragging: false,
  };

  // §2.4's capture-commit: quaternion at drag start; commit ONLY if it moved
  // and the gesture wasn't cancelled. reset() restores the start state, so an
  // Escaped drag compares equal and commits nothing.
  let dragStart: { x: number; y: number; z: number; w: number } | null = null;
  let cancelled = false;
  const onDraggingChanged = (event: { value: unknown }): void => {
    const dragging = event.value === true;
    probe.dragging = dragging;
    deps.setOrbitEnabled(!dragging);
    const target = gizmo.object;
    if (dragging && target) {
      const q = target.quaternion;
      dragStart = { x: q.x, y: q.y, z: q.z, w: q.w };
      cancelled = false;
      return;
    }
    if (!dragging && target && dragStart && !cancelled) {
      const q = target.quaternion.clone().normalize(); // the unit-quat gate expects units
      const moved =
        Math.abs(q.x - dragStart.x) + Math.abs(q.y - dragStart.y) + Math.abs(q.z - dragStart.z) + Math.abs(q.w - dragStart.w) >
        1e-9;
      if (moved && probe.selected) {
        config.onCommit(probe.selected, { x: q.x, y: q.y, z: q.z, w: q.w });
      }
    }
    dragStart = null;
  };
  gizmo.addEventListener('dragging-changed', onDraggingChanged as never);

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !probe.dragging) return;
    // Escape-to-cancel is FREE (§2.4): reset restores the drag-start state and
    // the compare-on-release sees no movement — the Document never knew.
    cancelled = true;
    gizmo.reset();
  };
  window.addEventListener('keydown', onKeyDown);

  // Click-to-pick: a press that travelled is a camera move, not a pick (the
  // 5px guard); a genuine click raycasts the handles through the REAL camera.
  const raycaster = new Raycaster();
  let pressAt: { x: number; y: number } | null = null;
  const onPointerDown = (event: PointerEvent): void => {
    if (config.readOnly) return;
    pressAt = { x: event.clientX, y: event.clientY };
  };
  const onPointerUp = (event: PointerEvent): void => {
    const start = pressAt;
    pressAt = null;
    if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5) return;
    if (probe.dragging) return; // the gizmo owns this gesture
    const rect = deps.domElement.getBoundingClientRect();
    const ndc = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(ndc, deps.camera);
    const hit = raycaster.intersectObjects([...visual.pickables], false)[0];
    if (!hit) return;
    const bone = hit.object.userData.bone as string;
    select(bone);
    config.onSelect(bone);
  };
  deps.domElement.addEventListener('pointerdown', onPointerDown);
  deps.domElement.addEventListener('pointerup', onPointerUp);

  function select(bone: string | null): void {
    if (config.readOnly) { probe.selected = bone; visual.setSelected(bone); return; }
    if (bone === probe.selected) return; // no re-attach churn (a mid-drag store notify must not grab the gizmo)
    probe.selected = bone;
    visual.setSelected(bone);
    if (bone === null) {
      gizmo.detach();
      return;
    }
    gizmo.attach(boneNodes.get(bone) as Object3D);
  }

  const world = new Vector3();
  return {
    probe,
    handleScreenXY(bone) {
      const node = boneNodes.get(bone);
      if (!node) return null;
      world.setFromMatrixPosition(node.matrixWorld).project(deps.camera);
      if (world.z > 1) return null; // behind the camera — not aimable
      const rect = deps.domElement.getBoundingClientRect();
      return {
        x: rect.left + ((world.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - world.y) / 2) * rect.height,
      };
    },
    update() {
      visual.update();
    },
    drawStates: () => visual.drawStates(),
    select,
    dispose() {
      // Withdrawn mid-drag (a mode chip clicked while a drag is live): the
      // preview is VIEW state and must not outlive the gesture (§2.4) — a
      // bone left at an uncommitted quaternion would be a pose the Document
      // never heard of, and the projector cannot heal a bone that was never
      // in the pose layer (Codex P2, #55). Restore the drag-start state first.
      if (dragStart !== null && gizmo.object) {
        gizmo.object.quaternion.set(dragStart.x, dragStart.y, dragStart.z, dragStart.w);
        dragStart = null;
        probe.dragging = false;
      }
      gizmo.removeEventListener('dragging-changed', onDraggingChanged as never);
      window.removeEventListener('keydown', onKeyDown);
      deps.domElement.removeEventListener('pointerdown', onPointerDown);
      deps.domElement.removeEventListener('pointerup', onPointerUp);
      gizmo.detach();
      deps.scene.remove(gizmo.getHelper());
      gizmo.dispose();
      visual.dispose();
      deps.setOrbitEnabled(true);
    },
  };
}
